import { useCallback, useEffect, useMemo, useState } from 'react';
import { Box, Loader2, RefreshCw } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import api from '@/lib/api';

const BASE = '/learning-tutorials/edm';

async function fetchRraoSummary(artifactId, theme = 'educational') {
  const res = await api.get(`${BASE}/artifacts/${artifactId}/rrao`, {
    params: { theme, include_svg: false },
  });
  const body = res.data;
  if (!body.success || !body.data) {
    throw new Error(body.error?.message || 'Failed to load RRAO');
  }
  return body.data;
}

function SummaryGrid({ summary }) {
  if (!summary) return null;
  const items = [
    ['Nodes', summary.node_count],
    ['Edges', summary.edge_count],
    ['Anchors', summary.anchor_count],
    ['Camera targets', summary.camera_target_count],
    ['Animation targets', summary.animation_target_count],
    ['Interaction zones', summary.interaction_zone_count],
    ['SVG size', summary.svg_length ? `${summary.svg_length} chars` : '—'],
  ];
  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 text-[11px]">
      {items.map(([label, value]) => (
        <div key={label} className="rounded border border-violet-500/15 bg-violet-950/10 px-2 py-1.5">
          <p className="text-slate-500">{label}</p>
          <p className="text-violet-100 font-mono">{value}</p>
        </div>
      ))}
    </div>
  );
}

export function ArtifactRraoV3Panel({ data }) {
  const entries = data?.rrao_entries || [];
  const [selectedId, setSelectedId] = useState('');
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const selectedEntry = useMemo(
    () => entries.find((e) => e.artifact_id === selectedId) || entries[0] || null,
    [entries, selectedId],
  );

  useEffect(() => {
    if (!entries.length) {
      setSelectedId('');
      return;
    }
    if (!selectedId || !entries.some((e) => e.artifact_id === selectedId)) {
      setSelectedId(entries[0].artifact_id);
    }
  }, [entries, selectedId]);

  const loadDetail = useCallback(async () => {
    const embedded = selectedEntry?.rrao_summary;
    if (embedded) {
      setDetail(embedded);
      setError(null);
      return;
    }
    const aid = selectedEntry?.artifact_id;
    if (!aid) {
      setDetail(null);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const payload = await fetchRraoSummary(aid);
      setDetail({
        node_count: payload.nodes?.length ?? 0,
        edge_count: payload.edges?.length ?? 0,
        anchor_count: payload.anchors?.length ?? 0,
        camera_target_count: payload.camera_targets?.length ?? 0,
        animation_target_count: payload.animation_targets?.length ?? 0,
        interaction_zone_count: payload.interaction_zones?.length ?? 0,
        svg_length: payload.svg?.length ?? 0,
        bounds: payload.bounds,
        viewport: payload.viewport,
        semantic_metadata: payload.semantic_metadata,
        focus_metadata: payload.focus_metadata,
      });
    } catch (exc) {
      setDetail(null);
      setError(exc?.response?.data?.detail || exc?.message || 'Failed to load RRAO');
    } finally {
      setLoading(false);
    }
  }, [selectedEntry]);

  useEffect(() => {
    loadDetail();
  }, [loadDetail]);

  if (!data) return null;

  return (
    <div className="space-y-4">
      <div className="rounded-xl border border-violet-500/25 bg-violet-950/15 p-4 flex flex-wrap items-center gap-4">
        <div className="flex items-center gap-2 text-sm text-violet-200">
          <Box className="w-4 h-4" />
          {data.total_rrao_entries} Runtime Rendered Artifact Objects
          {data.step === '5C.6' ? (
            <span className="text-[10px] text-violet-400/70">(Step 5C.6 · RRAO)</span>
          ) : null}
        </div>
        <div className="text-sm text-slate-400">
          Positioned EDM + SVG + ARP → runtime package for player & narration
        </div>
      </div>

      <div className="rounded-lg border border-slate-700/60 bg-slate-900/40 p-3 text-[11px] text-slate-400 font-mono space-y-1">
        <p>5C.5 SVG → <span className="text-violet-300">5C.6 RRAO</span> → Tutorial Player</p>
        <p>Formats: {(data.serialization_formats || []).join(' · ')}</p>
        <p>Consumers: {(data.consumers || []).slice(0, 4).join(' · ')}…</p>
      </div>

      {entries.length ? (
        <div className="space-y-3">
          <div className="flex flex-wrap items-center gap-2">
            {entries.length > 1 ? (
              <Select value={selectedEntry?.artifact_id || ''} onValueChange={setSelectedId}>
                <SelectTrigger className="h-8 min-w-[200px] text-xs border-slate-700 bg-slate-900 text-slate-300">
                  <SelectValue placeholder="Select artifact" />
                </SelectTrigger>
                <SelectContent>
                  {entries.map((e) => (
                    <SelectItem key={e.artifact_id} value={e.artifact_id}>
                      {e.artifact_id}
                      {' · '}
                      {e.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            ) : (
              <span className="text-xs text-slate-400 font-mono">{selectedEntry?.artifact_id}</span>
            )}
            <Button
              type="button"
              size="sm"
              variant="ghost"
              className="h-8 text-xs text-slate-400"
              disabled={loading}
              onClick={loadDetail}
            >
              <RefreshCw className={`w-3 h-3 mr-1 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>

          {loading ? (
            <div className="flex items-center gap-2 text-sm text-slate-400 py-6">
              <Loader2 className="w-4 h-4 animate-spin text-violet-400" />
              Building RRAO preview…
            </div>
          ) : error ? (
            <p className="text-sm text-amber-300/90 rounded-lg border border-amber-500/20 bg-amber-950/15 px-3 py-4">
              {error}
            </p>
          ) : (
            <SummaryGrid summary={detail || selectedEntry?.rrao_summary} />
          )}

          {detail?.focus_metadata?.most_important?.length ? (
            <div className="rounded-lg border border-violet-500/15 bg-violet-950/10 px-3 py-2 text-[11px]">
              <p className="text-slate-500 mb-1">Focus concepts</p>
              <p className="text-violet-200 font-mono">{detail.focus_metadata.most_important.join(' · ')}</p>
            </div>
          ) : null}
        </div>
      ) : (
        <p className="text-sm text-slate-500">No RRAO entries yet.</p>
      )}

      {entries.length ? (
        <ul className="space-y-1 pt-1 border-t border-slate-800/80">
          {entries.map((e) => (
            <li
              key={e.artifact_id}
              className="rounded-lg border border-violet-500/20 bg-violet-950/10 px-3 py-2 text-sm text-violet-100"
            >
              <span className="font-mono text-[11px] text-violet-400/90">{e.artifact_id}</span>
              <span className="mx-2 text-slate-500">·</span>
              <span className="truncate">{e.name}</span>
              <span className="text-[10px] text-slate-500 ml-2">{e.artifact_type}</span>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}
